import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { listCompanies } from '@/api/companies'
import {
  generateOrderDkp,
  getDkpDocumentSignedUrl,
  getLatestDkpDocument,
} from '@/api/dkpGenerator'
import { listOrders } from '@/api/orders'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { emptyTableCellClassName } from '@/constants/ui'
import type { OrderStatus } from '@/types/enums'
import { statusLabelRu } from '@/utils/status-transitions'

/**
 * Отладочная страница автогенерации ДКП.
 *
 * Список заказов в статусах, где у нас уже есть все нужные данные
 * (паспорт продавца, ПТС, согласованная цена, привязка к компании-заказчику
 * с загруженным шаблоном ДКП). Кнопка «Сгенерировать ДКП» шлёт запрос
 * в pdfgen → получаем PDF + запись в `order_documents`. Кнопка «Скачать
 * последний» открывает signed URL на PDF из bucket `documents`.
 *
 * До этого тут была ручная страница «загрузи .docx → заполни поля → PDF» —
 * вынесена в пользу автогенерации (см. CHANGELOG_ADMIN.md).
 */
// Воронка после согласования цены: BUYING → DOCUMENT_PREPARATION → PAYMENT_TRANSFER
// → TRANSPORTATION → COMPLETED. На всех этих статусах паспорт продавца, ПТС, цена
// и привязка к компании-заказчику уже есть, поэтому ДКП можно собирать.
// CANCELLED — отдельный архивный режим: бывают отменённые сделки с уже собранными
// данными, и иногда нужно перегенерить такой ДКП задним числом.
const ALLOWED_STATUSES: OrderStatus[] = [
  'BUYING',
  'DOCUMENT_PREPARATION',
  'PAYMENT_TRANSFER',
  'TRANSPORTATION',
  'COMPLETED',
  'CANCELLED',
]

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function DkpGeneratorPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [companyId, setCompanyId] = useState<string>('all')
  const [status, setStatus] = useState<OrderStatus | 'all'>('all')
  const [actionError, setActionError] = useState<string | null>(null)

  const { data: companies = { items: [], total: 0 } } = useQuery({
    queryKey: ['companies', 'customer-active'],
    queryFn: () =>
      listCompanies({ type: 'customer', pageSize: 100, includeInactive: false }),
  })
  const companyById = useMemo(() => {
    const m = new Map<string, (typeof companies.items)[number]>()
    for (const c of companies.items) m.set(c.id, c)
    return m
  }, [companies.items])

  const statusFilter: OrderStatus[] = status === 'all' ? ALLOWED_STATUSES : [status]
  const {
    data: ordersResult = { items: [], total: 0 },
    isLoading: ordersLoading,
  } = useQuery({
    queryKey: ['dkp-orders', companyId, statusFilter.join(',')],
    queryFn: () =>
      listOrders({
        status: statusFilter,
        companyId: companyId === 'all' ? undefined : companyId,
        pageSize: 100,
        dateField: 'created_at',
      }),
  })

  const generateMutation = useMutation({
    mutationFn: (orderId: string) => generateOrderDkp(orderId),
    onSuccess: (result, orderId) => {
      downloadBlob(result.pdf, `dkp_${orderId}.pdf`)
      void qc.invalidateQueries({ queryKey: ['latest-dkp', orderId] })
    },
    onError: (e: unknown) => {
      setActionError(e instanceof Error ? e.message : t('dkpgen.generateError'))
    },
  })

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-2">
        <h1 className="stryzh-heading-page">{t('dkpgen.title')}</h1>
        <p className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('dkpgen.subtitle')}
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <span className="stryzh-input-label">{t('dkpgen.filter.company')}</span>
          <Select<string>
            value={companyId}
            onChange={(v) => setCompanyId(v || 'all')}
            options={[
              { value: 'all', label: t('dkpgen.filter.allCompanies') },
              ...companies.items.map((c) => ({
                value: c.id,
                label: c.dkp_template_path ? c.name : t('dkpgen.filter.noTemplateSuffix', { name: c.name }),
              })),
            ]}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="stryzh-input-label">{t('dkpgen.filter.status')}</span>
          <Select<OrderStatus | 'all'>
            value={status}
            onChange={(v) => setStatus((v || 'all') as OrderStatus | 'all')}
            options={[
              { value: 'all', label: t('dkpgen.filter.allStatuses') },
              ...ALLOWED_STATUSES.map((s) => ({ value: s, label: statusLabelRu(s) })),
            ]}
          />
        </div>
      </div>

      {actionError ? (
        <div
          role="alert"
          className="rounded-md border border-[color:var(--stryzh-color-accent-red)] p-3 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-red)]"
        >
          {actionError}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-md border border-[color:var(--stryzh-color-border)]">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-[color:var(--stryzh-color-text-secondary)]">
              <th className="px-3 py-2">{t('dkpgen.col.number')}</th>
              <th className="px-3 py-2">{t('dkpgen.col.status')}</th>
              <th className="px-3 py-2">{t('dkpgen.col.company')}</th>
              <th className="px-3 py-2">{t('dkpgen.col.seller')}</th>
              <th className="px-3 py-2">{t('dkpgen.col.car')}</th>
              <th className="px-3 py-2">{t('dkpgen.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {ordersLoading ? (
              <tr>
                <td className={emptyTableCellClassName} colSpan={6}>
                  {t('common.loading')}
                </td>
              </tr>
            ) : ordersResult.items.length === 0 ? (
              <tr>
                <td className={emptyTableCellClassName} colSpan={6}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              ordersResult.items.map((o) => {
                const company = o.customer_company_id
                  ? companyById.get(o.customer_company_id)
                  : null
                const hasTemplate = !!company?.dkp_template_path
                const seller = o.seller_full_name ?? '—'
                const auto = [o.brand, o.model].filter(Boolean).join(' ') || '—'

                return (
                  <DkpOrderRow
                    key={o.id}
                    orderId={o.id}
                    statusLabel={statusLabelRu(o.status)}
                    companyName={company?.name ?? '—'}
                    sellerName={seller}
                    auto={auto}
                    hasTemplate={hasTemplate}
                    onGenerate={() => {
                      setActionError(null)
                      generateMutation.mutate(o.id)
                    }}
                    isGenerating={
                      generateMutation.isPending &&
                      generateMutation.variables === o.id
                    }
                  />
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Одна строка списка заказов с собственным сабзапросом «последний ДКП». */
function DkpOrderRow(props: {
  orderId: string
  statusLabel: string
  companyName: string
  sellerName: string
  auto: string
  hasTemplate: boolean
  onGenerate: () => void
  isGenerating: boolean
}) {
  const { orderId, statusLabel, companyName, sellerName, auto, hasTemplate, onGenerate, isGenerating } = props
  const { t } = useTranslation()

  const { data: latestDkp } = useQuery({
    queryKey: ['latest-dkp', orderId],
    queryFn: () => getLatestDkpDocument(orderId),
  })

  async function handleDownloadLatest() {
    if (!latestDkp) return
    const url = await getDkpDocumentSignedUrl(latestDkp.pdf_path)
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <tr className="border-t border-[color:var(--stryzh-color-border)]">
      <td className="px-3 py-2 font-mono text-xs">{orderId.slice(0, 8)}</td>
      <td className="px-3 py-2">{statusLabel}</td>
      <td className="px-3 py-2">{companyName}</td>
      <td className="px-3 py-2">{sellerName}</td>
      <td className="px-3 py-2">{auto}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onGenerate}
            disabled={!hasTemplate || isGenerating}
            title={
              hasTemplate
                ? t('dkpgen.generateTitle')
                : t('dkpgen.noTemplateTitle')
            }
          >
            {isGenerating ? t('dkpgen.generating') : t('dkpgen.generate')}
          </Button>
          {latestDkp ? (
            <Button variant="outline" onClick={handleDownloadLatest}>
              {t('dkpgen.downloadLatest')}
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  )
}
